import {useCallback, useEffect, useMemo, useState} from 'react'
import {
  Boxes,
  Building2,
  CheckCircle2,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import {Modal} from 'react-bootstrap'
import Select from 'react-select'

import * as API from '../../../api'
import CompanyBulkAccessModal from '../CompanyBulkAccessModal'

const companyInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'EM'

const getCompanyType = (company: any) => {
  if (company.type_name) return company.type_name
  if (typeof company.company_type === 'string') return company.company_type
  if (`${company.id_type_company}` === '1') return 'Indústria'
  if (`${company.id_type_company}` === '2') return 'Distribuidor'
  return 'Outro tipo'
}

const normalizeFeatureData = (payload: any) => {
  const companys = Array.isArray(payload?.companys) ? payload.companys : []
  const features = Array.isArray(payload?.features) ? payload.features : []
  const companyIds = new Set(companys.map((company: any) => `${company.id_company}`))
  const featureIds = new Set(features.map((feature: any) => `${feature.id_feature}`))
  const uniquePermissions = new Map<string, any>()

  ;(payload?.permissions || []).forEach((permission: any) => {
    if (
      !companyIds.has(`${permission.id_company}`) ||
      !featureIds.has(`${permission.id_feature}`)
    ) {
      return
    }
    uniquePermissions.set(`${permission.id_company}-${permission.id_feature}`, permission)
  })

  return {...payload, companys, features, permissions: Array.from(uniquePermissions.values())}
}

export const CompanyFeatureMatrix = () => {
  const [menu, setMenu] = useState<any>()
  const [companies, setCompanies] = useState<any[]>([])
  const [selectedCompanies, setSelectedCompanies] = useState<any[]>([])
  const [featureQuery, setFeatureQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [accessFilter, setAccessFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [savingCells, setSavingCells] = useState<Set<string>>(new Set())
  const [bulkSaving, setBulkSaving] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [selectedMatrixCompany, setSelectedMatrixCompany] = useState<any>(null)
  const [companyBulkSaving, setCompanyBulkSaving] = useState(false)
  const [confirmModal, setConfirmModal] = useState<any>({
    isOpen: false,
    feature: null,
    action: 'activate' as 'activate' | 'deactivate',
    count: 0,
  })

  const getFeatures = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true)
    setLoadError(false)

    try {
      const result = await API.getFeatures()
      const normalizedData = normalizeFeatureData(result?.data || {})
      setMenu(normalizedData)
      setCompanies(
        normalizedData.companys.map((company: any) => ({
          value: company.guid,
          label: company.name,
          ...company,
        }))
      )
    } catch {
      setLoadError(true)
    } finally {
      if (showLoading) setLoading(false)
    }
  }, [])

  useEffect(() => {
    getFeatures()
  }, [getFeatures])

  const filteredFeatures = useMemo(() => {
    const normalizedQuery = featureQuery.trim().toLocaleLowerCase('pt-BR')
    if (!normalizedQuery) return menu?.features || []
    return (menu?.features || []).filter((feature: any) =>
      `${feature.title || ''} ${feature.name || ''}`
        .toLocaleLowerCase('pt-BR')
        .includes(normalizedQuery)
    )
  }, [featureQuery, menu?.features])

  const hasPermission = useCallback(
    (companyId: number, featureId: number) =>
      Boolean(
        menu?.permissions?.some(
          (permission: any) =>
            `${permission.id_feature}` === `${featureId}` &&
            `${permission.id_company}` === `${companyId}`
        )
      ),
    [menu?.permissions]
  )

  const displayedCompanies = useMemo(() => {
    const baseCompanies = selectedCompanies.length ? selectedCompanies : menu?.companys || []
    const byType = baseCompanies.filter(
      (company: any) => typeFilter === 'all' || `${company.id_type_company}` === `${typeFilter}`
    )

    if (accessFilter === 'all' || !filteredFeatures.length) return byType

    return byType.filter((company: any) => {
      const accessCount = filteredFeatures.filter((feature: any) =>
        hasPermission(company.id_company, feature.id_feature)
      ).length
      if (accessFilter === 'full') return accessCount === filteredFeatures.length
      if (accessFilter === 'partial')
        return accessCount > 0 && accessCount < filteredFeatures.length
      return accessCount === 0
    })
  }, [accessFilter, filteredFeatures, hasPermission, menu?.companys, selectedCompanies, typeFilter])

  const companyTypes = useMemo(() => {
    const unique = new Map<string, string>()
    ;(menu?.companys || []).forEach((company: any) => {
      unique.set(`${company.id_type_company}`, getCompanyType(company))
    })
    return Array.from(unique.entries())
  }, [menu?.companys])

  function openFeatureBulk(feature: any) {
    if (!displayedCompanies.length) return
    const allChecked = displayedCompanies.every((company: any) =>
      hasPermission(company.id_company, feature.id_feature)
    )
    setConfirmModal({
      isOpen: true,
      feature,
      action: allChecked ? 'deactivate' : 'activate',
      count: displayedCompanies.length,
    })
  }

  async function changeFeatureForVisibleCompanies() {
    const {feature, action} = confirmModal
    if (!feature || !displayedCompanies.length) return
    setBulkSaving(true)
    setFeedback('')

    try {
      const responses = await Promise.all(
        displayedCompanies.map((company: any) =>
          action === 'activate'
            ? API.addFeatureCompanyPermission({
                id_feature: feature.id_feature,
                id_company: company.id_company,
              })
            : API.removeFeatureCompanyPermission({
                id_feature: feature.id_feature,
                id_company: company.id_company,
              })
        )
      )
      if (responses.some((response: any) => response?.success === false)) {
        throw new Error('Bulk feature update failed')
      }
      await getFeatures(false)
      setFeedback(
        `${feature.title || feature.name} foi ${
          action === 'activate' ? 'liberada' : 'removida'
        } para ${displayedCompanies.length} empresa(s).`
      )
    } catch {
      await getFeatures(false)
      setFeedback('Não foi possível concluir a alteração em massa. Tente novamente.')
    } finally {
      setBulkSaving(false)
      setConfirmModal({isOpen: false, feature: null, action: 'activate', count: 0})
    }
  }

  async function changePermission(feature: any, company: any, checked: boolean) {
    const cellKey = `${feature.id_feature}-${company.id_company}`
    const previousPermissions = menu.permissions || []
    const permission = {id_feature: feature.id_feature, id_company: company.id_company}
    setSavingCells((current) => new Set(current).add(cellKey))
    setFeedback('')
    setMenu((current: any) => ({
      ...current,
      permissions: checked
        ? [
            ...current.permissions.filter(
              (item: any) =>
                !(
                  `${item.id_feature}` === `${feature.id_feature}` &&
                  `${item.id_company}` === `${company.id_company}`
                )
            ),
            permission,
          ]
        : current.permissions.filter(
            (item: any) =>
              !(
                `${item.id_feature}` === `${feature.id_feature}` &&
                `${item.id_company}` === `${company.id_company}`
              )
          ),
    }))

    try {
      const response = checked
        ? await API.addFeatureCompanyPermission(permission)
        : await API.removeFeatureCompanyPermission(permission)
      if (response?.success === false) throw new Error('Feature update failed')
      await getFeatures(false)
    } catch {
      setMenu((current: any) => ({...current, permissions: previousPermissions}))
      await getFeatures(false)
      setFeedback('Não foi possível salvar uma das alterações. Tente novamente.')
    } finally {
      setSavingCells((current) => {
        const next = new Set(current)
        next.delete(cellKey)
        return next
      })
    }
  }

  async function changeCompanyFeatures(selectAll: boolean) {
    if (!selectedMatrixCompany) return
    const targets = (menu?.features || []).filter(
      (feature: any) =>
        hasPermission(selectedMatrixCompany.id_company, feature.id_feature) !== selectAll
    )
    setCompanyBulkSaving(true)
    setFeedback('')

    try {
      const responses = await Promise.all(
        targets.map((feature: any) =>
          selectAll
            ? API.addFeatureCompanyPermission({
                id_feature: feature.id_feature,
                id_company: selectedMatrixCompany.id_company,
              })
            : API.removeFeatureCompanyPermission({
                id_feature: feature.id_feature,
                id_company: selectedMatrixCompany.id_company,
              })
        )
      )
      if (responses.some((response: any) => response?.success === false)) {
        throw new Error('Bulk company feature update failed')
      }
      await getFeatures(false)
      setFeedback(
        `${
          selectAll
            ? 'Todas as funcionalidades foram concedidas'
            : 'Todas as funcionalidades foram removidas'
        } para ${selectedMatrixCompany.name}.`
      )
      setSelectedMatrixCompany(null)
    } catch {
      await getFeatures(false)
      setFeedback('Não foi possível concluir a alteração para esta empresa. Tente novamente.')
    } finally {
      setCompanyBulkSaving(false)
    }
  }

  const permissionCount = menu?.permissions?.length || 0
  const possiblePermissions = (menu?.companys?.length || 0) * (menu?.features?.length || 0)
  const coverage = possiblePermissions
    ? Math.round((permissionCount / possiblePermissions) * 100)
    : 0
  const selectedCompanyActiveCount = selectedMatrixCompany
    ? (menu?.features || []).filter((feature: any) =>
        hasPermission(selectedMatrixCompany.id_company, feature.id_feature)
      ).length
    : 0
  const hasFilters = Boolean(
    selectedCompanies.length || featureQuery || typeFilter !== 'all' || accessFilter !== 'all'
  )

  return (
    <section className='ua-section'>
      <Modal
        id='company_features_row_modal'
        tabIndex={-1}
        dialogClassName='modal-dialog modal-dialog-centered mw-500px ua-admin-modal-dialog'
        show={Boolean(selectedMatrixCompany)}
        onHide={() => !companyBulkSaving && setSelectedMatrixCompany(null)}
      >
        {selectedMatrixCompany && (
          <CompanyBulkAccessModal
            company={selectedMatrixCompany}
            scopeLabel='Funcionalidades'
            itemLabel='funcionalidades'
            totalCount={menu?.features?.length || 0}
            activeCount={selectedCompanyActiveCount}
            loading={companyBulkSaving}
            onChange={changeCompanyFeatures}
            onClose={() => setSelectedMatrixCompany(null)}
          />
        )}
      </Modal>

      <Modal
        id='company_feature_column_modal'
        tabIndex={-1}
        dialogClassName='modal-dialog modal-dialog-centered mw-450px ua-admin-modal-dialog'
        show={confirmModal.isOpen}
        onHide={() => !bulkSaving && setConfirmModal({...confirmModal, isOpen: false})}
      >
        <div className='ca-confirm-modal'>
          <div className='ca-confirm-modal__body'>
            <span className='ca-confirm-modal__icon'>
              <SlidersHorizontal size={21} />
            </span>
            <h3>
              {confirmModal.action === 'activate'
                ? 'Conceder funcionalidade?'
                : 'Remover funcionalidade?'}
            </h3>
            <p>
              A funcionalidade{' '}
              <strong>{confirmModal.feature?.title || confirmModal.feature?.name}</strong> será{' '}
              {confirmModal.action === 'activate' ? 'concedida' : 'removida'} para{' '}
              <strong>{confirmModal.count} empresa(s) visível(is)</strong>.
            </p>
          </div>
          <footer className='ca-confirm-modal__footer'>
            <button
              type='button'
              className='ua-secondary-button'
              disabled={bulkSaving}
              onClick={() => setConfirmModal({...confirmModal, isOpen: false})}
            >
              Cancelar
            </button>
            <button
              type='button'
              className={
                confirmModal.action === 'activate' ? 'ua-primary-button' : 'ua-danger-button'
              }
              disabled={bulkSaving}
              onClick={changeFeatureForVisibleCompanies}
            >
              {bulkSaving ? <span className='ua-spinner' /> : <CheckCircle2 size={14} />}
              Confirmar alteração
            </button>
          </footer>
        </div>
      </Modal>

      <header className='ua-section__heading'>
        <div className='ua-section__heading-copy'>
          <span className='ua-section__heading-icon'>
            <Boxes size={20} />
          </span>
          <div>
            <h2>Funcionalidades das empresas</h2>
            <p>Libere recursos complementares por organização, individualmente ou em massa.</p>
          </div>
        </div>
        <span className='ua-badge ua-badge--success'>Alterações salvas automaticamente</span>
      </header>

      {loading ? (
        <div className='ua-card ua-loading'>
          <span className='ua-spinner' /> Carregando funcionalidades…
        </div>
      ) : loadError ? (
        <div className='ua-empty'>
          <div>
            <span className='ua-empty__icon'>
              <Boxes size={22} />
            </span>
            <h3>Não foi possível carregar as funcionalidades</h3>
            <p>Tente novamente para acessar a matriz de recursos.</p>
            <button
              type='button'
              className='ua-secondary-button mt-4'
              onClick={() => getFeatures()}
            >
              Tentar novamente
            </button>
          </div>
        </div>
      ) : menu?.features?.length ? (
        <>
          <div className='ua-stats'>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <Building2 size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{menu.companys?.length || 0}</strong>
                <small className='ua-stat__label'>Empresas na matriz</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <Boxes size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{menu.features.length}</strong>
                <small className='ua-stat__label'>Funcionalidades disponíveis</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <CheckCircle2 size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{permissionCount}</strong>
                <small className='ua-stat__label'>Acessos concedidos</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <ShieldCheck size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{coverage}%</strong>
                <small className='ua-stat__label'>Cobertura de acesso</small>
              </span>
            </div>
          </div>

          <div className='ua-toolbar'>
            <div className='ua-field ua-field--wide'>
              <label htmlFor='feature-company-filter'>Empresas</label>
              <Select
                inputId='feature-company-filter'
                isMulti
                options={companies}
                value={selectedCompanies}
                closeMenuOnSelect={false}
                placeholder='Todas as empresas'
                noOptionsMessage={() => 'Nenhuma empresa encontrada'}
                onChange={(selected: any) => setSelectedCompanies(selected || [])}
                classNamePrefix='admin-select'
              />
            </div>
            <div className='ua-field'>
              <label htmlFor='feature-company-query'>Funcionalidade</label>
              <div className='ua-search'>
                <Search size={15} />
                <input
                  id='feature-company-query'
                  value={featureQuery}
                  placeholder='Buscar recurso'
                  onChange={(event) => setFeatureQuery(event.target.value)}
                />
                {featureQuery && (
                  <button
                    type='button'
                    className='ua-search__clear'
                    aria-label='Limpar busca de funcionalidade'
                    onClick={() => setFeatureQuery('')}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className='ua-field'>
              <label htmlFor='feature-company-type'>Tipo</label>
              <div className='ua-select'>
                <select
                  id='feature-company-type'
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}
                >
                  <option value='all'>Todos os tipos</option>
                  {companyTypes.map(([id, label]) => (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className='ua-field'>
              <label htmlFor='feature-company-access'>Nível de acesso</label>
              <div className='ua-select'>
                <select
                  id='feature-company-access'
                  value={accessFilter}
                  onChange={(event) => setAccessFilter(event.target.value)}
                >
                  <option value='all'>Todos</option>
                  <option value='full'>Acesso completo</option>
                  <option value='partial'>Acesso parcial</option>
                  <option value='none'>Sem acesso</option>
                </select>
              </div>
            </div>
            {hasFilters && (
              <button
                type='button'
                className='ua-secondary-button'
                onClick={() => {
                  setSelectedCompanies([])
                  setFeatureQuery('')
                  setTypeFilter('all')
                  setAccessFilter('all')
                }}
              >
                <X size={14} /> Limpar
              </button>
            )}
          </div>

          {feedback && (
            <div
              className={`ua-feedback ${
                feedback.startsWith('Não foi possível') ? 'ua-feedback--danger' : ''
              }`}
            >
              {feedback}
            </div>
          )}

          <div className='ua-card'>
            <div className='ua-card__header'>
              <div>
                <h3>Matriz de funcionalidades</h3>
                <p>
                  {displayedCompanies.length} empresa(s) · {filteredFeatures.length}{' '}
                  funcionalidade(s)
                </p>
              </div>
              <span className='ua-matrix-tip'>
                Clique em uma coluna ou empresa para aplicar em massa
              </span>
            </div>

            {displayedCompanies.length && filteredFeatures.length ? (
              <div className='ua-table-wrap ua-matrix-wrap'>
                <table className='ua-table ua-matrix'>
                  <thead>
                    <tr>
                      <th className='ua-sticky-column'>Empresa</th>
                      {filteredFeatures.map((feature: any) => (
                        <th key={feature.id_feature} className='ua-matrix__permission'>
                          <button
                            type='button'
                            className='ua-matrix-header-button'
                            title={`Aplicar ${feature.title || feature.name} às empresas visíveis`}
                            onClick={() => openFeatureBulk(feature)}
                          >
                            <SlidersHorizontal size={13} />
                            <span>{feature.title || feature.name}</span>
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayedCompanies.map((company: any) => (
                      <tr key={company.guid || company.id_company}>
                        <td className='ua-sticky-column'>
                          <button
                            type='button'
                            className='ua-user-trigger'
                            title={`Gerenciar todas as funcionalidades de ${company.name}`}
                            onClick={() => setSelectedMatrixCompany(company)}
                          >
                            <span className='ua-user-cell'>
                              <span className='ua-avatar'>{companyInitials(company.name)}</span>
                              <span className='ua-user-cell__copy'>
                                <strong>{company.name}</strong>
                                <small>{getCompanyType(company)}</small>
                              </span>
                            </span>
                            <span className='ua-user-trigger__action'>
                              <SlidersHorizontal size={14} />
                            </span>
                          </button>
                        </td>
                        {filteredFeatures.map((feature: any) => {
                          const cellKey = `${feature.id_feature}-${company.id_company}`
                          return (
                            <td key={cellKey} className='ua-matrix__cell'>
                              <label className='ua-switch'>
                                <input
                                  type='checkbox'
                                  checked={hasPermission(company.id_company, feature.id_feature)}
                                  disabled={savingCells.has(cellKey)}
                                  aria-label={`${feature.title || feature.name} para ${
                                    company.name
                                  }`}
                                  onChange={(event) =>
                                    changePermission(feature, company, event.target.checked)
                                  }
                                />
                                <span />
                              </label>
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='ua-empty ua-empty--inside'>
                <div>
                  <span className='ua-empty__icon'>
                    <Search size={22} />
                  </span>
                  <h3>Nenhum resultado nesta matriz</h3>
                  <p>Ajuste os filtros para visualizar outras combinações de acesso.</p>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className='ua-empty'>
          <div>
            <span className='ua-empty__icon'>
              <Boxes size={22} />
            </span>
            <h3>Nenhuma funcionalidade disponível</h3>
            <p>Não existem recursos complementares configurados para as empresas.</p>
          </div>
        </div>
      )}
    </section>
  )
}
